/**
 * RESPONSIBILITY: the annotations that go on a FIELD.
 *
 * Each one states its own type, because `emitDecoratorMetadata` is unavailable under
 * esbuild and the declared TypeScript type cannot be reflected. `label` is optional
 * everywhere — omit it and the property name is humanized (`blueDeep` → "Blue Deep").
 *
 * Adding a field type is: one line here, one case in the four generators, one
 * renderer. No `switch (kind)` ever appears in the rendering path — see ui-schema.ts.
 */
import { pushField } from './registry';
import type { CommonFieldOptions as Common } from './types';

export const Text = (o: Common & { maxLength?: number; minLength?: number; default?: string } = {}): PropertyDecorator =>
  (t, k) => pushField(t, { key: String(k), kind: 'text', required: false, ...o });

/**
 * Type-less annotation. Say nothing about the kind and you get text — the default.
 * `@Field({ label: 'Heading' })` is exactly `@Text({ label: 'Heading' })`.
 */
export const Field = (o: Common & { maxLength?: number; minLength?: number; default?: string } = {}): PropertyDecorator =>
  (t, k) => pushField(t, { key: String(k), kind: 'text', required: false, ...o });

/**
 * Multi-line text. `markdown: true` means the page renders it with <Markdown> — bold,
 * italic, lists, links, and whatever directives the app registered. What is stored is
 * unchanged either way, so the flag can be turned on later without a migration.
 */
export const Paragraph = (
  o: Common & {
    maxLength?: number; rows?: number; default?: string;
    markdown?: boolean; allow?: readonly string[];
  } = {},
): PropertyDecorator =>
  (t, k) => pushField(t, { key: String(k), kind: 'paragraph', required: false, rows: 4, ...o });

export const Color = (o: Common & { default?: string } = {}): PropertyDecorator =>
  (t, k) => pushField(t, { key: String(k), kind: 'color', required: false, ...o });

export const Img = (o: Common & { folder?: string; maxSizeMb?: number; size?: string; default?: string } = {}): PropertyDecorator =>
  (t, k) => pushField(t, { key: String(k), kind: 'img', required: false, maxSizeMb: 12, ...o });

/**
 * A video file, uploaded the same way a picture is.
 *
 * It STORES THE SAME SHAPE as @Img — a path and the words that go with it — because it is
 * the same fact: a reference to one file that is never renamed, plus a description that
 * belongs to this place on the page and not to the file. Giving video a value type of its
 * own would double the schema, the validator and the defaults to say nothing new.
 *
 * `maxSizeMb` defaults high because video is not a photograph, and the server caps it
 * regardless — see server/routes/upload-routes.ts.
 */
export const Video = (o: Common & { folder?: string; maxSizeMb?: number; size?: string } = {}): PropertyDecorator =>
  (t, k) => pushField(t, { key: String(k), kind: 'video', required: false, maxSizeMb: 24, ...o });

export const DateField = (o: Common & { time?: boolean } = {}): PropertyDecorator =>
  (t, k) => pushField(t, { key: String(k), kind: 'date', required: false, ...o });

/**
 * A repeating date — every Tuesday at seven, the third Friday, the 25th of December.
 * Chosen from a picker, never typed; stored as RRULE text. When it next falls is computed
 * from it, not entered. See core/repeat-rule.ts.
 */
export const Repeats = (o: Common = {}): PropertyDecorator =>
  (t, k) => pushField(t, { key: String(k), kind: 'repeat', required: false, ...o });

export const Num = (o: Common & { min?: number; max?: number; integer?: boolean; default?: number } = {}): PropertyDecorator =>
  (t, k) => pushField(t, { key: String(k), kind: 'num', required: false, ...o });

export const Bool = (o: Common & { default?: boolean } = {}): PropertyDecorator =>
  (t, k) => pushField(t, { key: String(k), kind: 'bool', required: false, ...o });

/**
 * One of a fixed list, or one of a list the site edits for itself.
 *
 *   @Choice({ options: ['a', 'b'] })        fixed at compile time
 *   @Choice({ in: 'categories' })           whatever `categories` holds right now
 *   @Choice({ in: 'categories', multiple: true })   several of them
 *
 * `in` is deliberately NOT baked into the schema: the vocabulary lives in the document,
 * so membership is enforced by validate() instead of by an enum. See core/lookup.ts.
 *
 * `as` changes only how it is DRAWN — a `<select>` becomes radios, chips become
 * checkboxes. Same stored value, same schema, same validation, so it can be turned on
 * later without a migration:
 *
 *   @Choice({ options: ['Yes', 'No'], as: 'radio' })
 *   @Choice({ in: 'wants', multiple: true, as: 'checkbox' })
 */
export const Choice = (
  o: Common & {
    options?: readonly string[]; in?: string; multiple?: boolean;
    as?: 'radio' | 'checkbox'; default?: string;
  },
): PropertyDecorator =>
  (t, k) => pushField(t, { key: String(k), kind: 'choice', required: false, ...o });

/**
 * A list of plain strings the site maintains itself — the vocabulary a @Choice points
 * at with `in`. Admin edits it; every field referencing it follows without a deploy.
 */
export const StringList = (o: Common & { maxItems?: number; default?: string[] } = {}): PropertyDecorator =>
  (t, k) => pushField(t, { key: String(k), kind: 'stringlist', required: false, ...o });

/**
 * The rendered form of a markdown field, written by the framework and never by hand.
 *
 *     @Paragraph({ markdown: true, allow: ['verse'] })  body!: string;
 *     @RenderOf('body')                                 bodyHtml!: string;
 *
 * Rendering once on save rather than on every view is what keeps the markdown parser
 * out of the browser bundle entirely. The value is ALWAYS recomputed, so a client may
 * send whatever it likes and have it thrown away — there is nothing here to strip.
 *
 * The argument is the property name of the source field, because that name already
 * exists; a second identity for the same field is what hard rule 12 is about. A name
 * with no markdown field behind it throws when sections register.
 */
export const RenderOf = (source: string, o: Common = {}): PropertyDecorator =>
  (t, k) => pushField(t, {
    key: String(k), kind: 'text', required: false, derived: true, renderOf: source, ...o,
  });

export const Link = (o: Common & { default?: string } = {}): PropertyDecorator =>
  (t, k) => pushField(t, { key: String(k), kind: 'link', required: false, ...o });

export const List = (o: Common & { of: () => Function; minItems?: number; maxItems?: number }): PropertyDecorator =>
  (t, k) => pushField(t, { key: String(k), kind: 'list', required: false, ...o });
